import {
  ColorInput,
  Divider,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  Slider,
  Stack,
  Switch,
  Tabs,
  Text,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconMessageDots, IconPalette, IconRobot, IconUser } from '@tabler/icons-react';
import {
  WebBotBackgroundImageMode,
  WebBotBackgroundTarget,
  WebBotBackgroundType,
  WebBotHeaderFooterStyle,
  WebBotProviderData,
} from '@/API';
import { GradientInput } from '../GradientInput';
import { WebBotAvatarUpload, WebBotBackgroundImageUpload } from '../WebBotImageUpload';

interface WebBotStyleTabProps {
  form: UseFormReturnType<WebBotProviderData>;
  workspaceId?: string;
  botProviderId?: string;
}

interface BubbleStyleEditorProps {
  path: 'userBubble' | 'botBubble';
  form: UseFormReturnType<WebBotProviderData>;
}

function BubbleStyleEditor({ path, form }: BubbleStyleEditorProps) {
  const isBot = path === 'botBubble';
  const bubble = form.values[path];

  return (
    <Stack gap="md">
      <Group grow align="flex-start">
        <ColorInput
          label="氣泡背景顏色"
          placeholder={isBot ? '預設 #e0e0e0 (留空則使用預設)' : '留空則使用主題預設色'}
          {...form.getInputProps(`${path}.color`)}
          value={bubble?.color ?? ''}
        />
        <ColorInput
          label="氣泡文字顏色"
          placeholder={isBot ? '預設 #333333 (留空則自動計算)' : '留空則自動計算'}
          {...form.getInputProps(`${path}.textColor`)}
          value={bubble?.textColor ?? ''}
        />
      </Group>

      <Group grow align="flex-start">
        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="xs" fw={500}>
              背景不透明度 (Opacity)
            </Text>
            <Text size="xs" c="dimmed">
              {bubble?.opacity ?? 100}%
            </Text>
          </Group>
          <Slider
            min={0}
            max={100}
            step={1}
            mb="md"
            marks={[
              { value: 0, label: '0%' },
              { value: 50, label: '50%' },
              { value: 100, label: '100%' },
            ]}
            {...form.getInputProps(`${path}.opacity`)}
            value={bubble?.opacity ?? 100}
          />
        </Stack>

        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="xs" fw={500}>
              透明模糊 (Backdrop Blur)
            </Text>
            <Text size="xs" c="dimmed">
              {bubble?.blur ?? 0}px
            </Text>
          </Group>
          <Slider
            min={0}
            max={30}
            step={1}
            mb="md"
            marks={[
              { value: 0, label: '0px' },
              { value: 15, label: '15px' },
              { value: 30, label: '30px' },
            ]}
            {...form.getInputProps(`${path}.blur`)}
            value={bubble?.blur ?? 0}
          />
        </Stack>
      </Group>

      <Paper withBorder p="sm" radius="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
        <Stack gap="xs">
          <Text size="xs" fw={600} c="dimmed">
            氣泡陰影設定 (Box Shadow)
          </Text>
          <ColorInput
            label="陰影顏色"
            placeholder="例如: rgba(0,0,0,0.1) 或 #00000020 (留空則無陰影)"
            {...form.getInputProps(`${path}.shadowColor`)}
            value={bubble?.shadowColor ?? ''}
          />
          <Group grow align="flex-start">
            <NumberInput
              label="水平位移 X (px)"
              placeholder="0"
              min={-50}
              max={50}
              {...form.getInputProps(`${path}.shadowOffsetX`)}
              value={bubble?.shadowOffsetX ?? 0}
            />
            <NumberInput
              label="垂直位移 Y (px)"
              placeholder="0"
              min={-50}
              max={50}
              {...form.getInputProps(`${path}.shadowOffsetY`)}
              value={bubble?.shadowOffsetY ?? 0}
            />
            <NumberInput
              label="陰影模糊柔度 Blur (px)"
              placeholder="0"
              min={0}
              max={50}
              {...form.getInputProps(`${path}.shadowBlur`)}
              value={bubble?.shadowBlur ?? 0}
            />
          </Group>
        </Stack>
      </Paper>

      <Paper withBorder p="sm" radius="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
        <Stack gap="xs">
          <Text size="xs" fw={600} c="dimmed">
            氣泡框線設定 (Border)
          </Text>
          <Group grow align="flex-start">
            <NumberInput
              label="框線粗細 (px)"
              placeholder="0 (無框線)"
              min={0}
              max={10}
              {...form.getInputProps(`${path}.borderWidth`)}
              value={bubble?.borderWidth ?? 0}
            />
            <ColorInput
              label="框線顏色"
              placeholder="例如: #e2e8f0 或 rgba(255,255,255,0.4)"
              {...form.getInputProps(`${path}.borderColor`)}
              value={bubble?.borderColor ?? ''}
            />
          </Group>
        </Stack>
      </Paper>

      <Paper withBorder p="sm" radius="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
        <Stack gap="sm">
          <Text size="xs" fw={600} c="dimmed">
            圓角造型設定 (Border Radius)
          </Text>
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="xs" fw={500}>
                主圓角半徑 (Border Radius)
              </Text>
              <Text size="xs" c="dimmed">
                {bubble?.borderRadius ?? 15}px
              </Text>
            </Group>
            <Slider
              min={0}
              max={50}
              step={1}
              mb="md"
              marks={[
                { value: 0, label: '0px' },
                { value: 15, label: '15px' },
                { value: 25, label: '25px' },
                { value: 50, label: '50px' },
              ]}
              {...form.getInputProps(`${path}.borderRadius`)}
              value={bubble?.borderRadius ?? 15}
            />
          </Stack>
          <Switch
            label={isBot ? '保留左下對話方向尾角 (4px Tail)' : '保留右下對話方向尾角 (4px Tail)'}
            description={
              isBot
                ? '開啟時左下角為小圓角以提示對話方向；關閉則四角均勻對稱圓角'
                : '開啟時右下角為小圓角以提示對話方向；關閉則四角均勻對稱圓角'
            }
            checked={bubble?.enableDirectionRadius ?? true}
            {...form.getInputProps(`${path}.enableDirectionRadius`, { type: 'checkbox' })}
          />
        </Stack>
      </Paper>
    </Stack>
  );
}

export function WebBotStyleTab({ form, workspaceId, botProviderId }: WebBotStyleTabProps) {
  const isBadgeEnabled = form.values.messageMeta?.enableBadge ?? false;

  return (
    <Stack gap="md">
      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Group gap="xs">
            <IconPalette size={18} color="var(--mantine-color-blue-6)" />
            <Text size="sm" fw={600}>
              主題與全域色彩設定
            </Text>
          </Group>
          <Divider style={{ opacity: 0.6 }} />
          <ColorInput
            withAsterisk
            label="主題顏色 (Theme Color)"
            {...form.getInputProps('theme.themeColor')}
            value={form.values.theme?.themeColor ?? ''}
          />
          <Group grow align="flex-start">
            <ColorInput
              label="機器人名稱顏色"
              placeholder="留空則自動適應"
              {...form.getInputProps('messageMeta.botNameColor')}
              value={form.values.messageMeta?.botNameColor ?? ''}
            />
            <ColorInput
              label="訊息輔助資訊與按鈕顏色"
              placeholder="留空則自動適應"
              {...form.getInputProps('messageMeta.metaColor')}
              value={form.values.messageMeta?.metaColor ?? ''}
            />
          </Group>
          <Switch
            label="啟用時間與按鈕膠囊底色 (Badge)"
            description="在時間與操作按鈕外層加上半透明模糊膠囊底色，提升複雜背景圖上的可讀性"
            checked={isBadgeEnabled}
            {...form.getInputProps('messageMeta.enableBadge', { type: 'checkbox' })}
          />

          {isBadgeEnabled && (
            <Paper withBorder p="sm" radius="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
              <Stack gap="sm">
                <Text size="xs" fw={600} c="dimmed">
                  時間與按鈕膠囊樣式設定 (Badge Styling)
                </Text>
                <ColorInput
                  label="膠囊背景顏色"
                  placeholder="預設 #ffffff (留空則使用預設白底)"
                  {...form.getInputProps('messageMeta.badgeBackgroundColor')}
                  value={form.values.messageMeta?.badgeBackgroundColor ?? ''}
                />
                <Group grow align="flex-start">
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="xs" fw={500}>
                        膠囊不透明度 (Opacity)
                      </Text>
                      <Text size="xs" c="dimmed">
                        {form.values.messageMeta?.badgeOpacity ?? 65}%
                      </Text>
                    </Group>
                    <Slider
                      min={0}
                      max={100}
                      step={1}
                      mb="md"
                      marks={[
                        { value: 0, label: '0%' },
                        { value: 50, label: '50%' },
                        { value: 65, label: '65%' },
                        { value: 100, label: '100%' },
                      ]}
                      {...form.getInputProps('messageMeta.badgeOpacity')}
                      value={form.values.messageMeta?.badgeOpacity ?? 65}
                    />
                  </Stack>

                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="xs" fw={500}>
                        透明模糊 (Blur)
                      </Text>
                      <Text size="xs" c="dimmed">
                        {form.values.messageMeta?.badgeBlur ?? 4}px
                      </Text>
                    </Group>
                    <Slider
                      min={0}
                      max={20}
                      step={1}
                      mb="md"
                      marks={[
                        { value: 0, label: '0px' },
                        { value: 4, label: '4px' },
                        { value: 10, label: '10px' },
                        { value: 20, label: '20px' },
                      ]}
                      {...form.getInputProps('messageMeta.badgeBlur')}
                      value={form.values.messageMeta?.badgeBlur ?? 4}
                    />
                  </Stack>
                </Group>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Group gap="xs">
              <IconMessageDots size={18} color="var(--mantine-color-blue-6)" />
              <Text size="sm" fw={600}>
                對話氣泡樣式 (Message Bubbles)
              </Text>
            </Group>
          </Group>
          <Divider style={{ opacity: 0.6 }} />

          <Tabs defaultValue="bot" variant="outline" radius="sm">
            <Tabs.List mb="md">
              <Tabs.Tab value="bot" leftSection={<IconRobot size={16} />}>
                AI 機器人訊息氣泡
              </Tabs.Tab>
              <Tabs.Tab value="user" leftSection={<IconUser size={16} />}>
                使用者訊息氣泡
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="bot">
              <BubbleStyleEditor path="botBubble" form={form} />
            </Tabs.Panel>

            <Tabs.Panel value="user">
              <BubbleStyleEditor path="userBubble" form={form} />
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Stack gap="md">
          <Text size="sm" fw={500}>
            背景樣式設定
          </Text>
          <Divider style={{ opacity: 0.6 }} />

          <Stack gap="xs">
            <Text size="xs" c="dimmed">
              背景類型
            </Text>
            <SegmentedControl
              fullWidth
              data={[
                { label: '純色 (Color)', value: WebBotBackgroundType.Color },
                { label: '漸層 (Gradient)', value: WebBotBackgroundType.Gradient },
                { label: '圖片 (Image)', value: WebBotBackgroundType.Image },
              ]}
              {...form.getInputProps('background.type')}
              value={form.values.background?.type ?? WebBotBackgroundType.Color}
              onChange={(val) => {
                form.setFieldValue('background.type', val as WebBotBackgroundType);
                if (val === WebBotBackgroundType.Gradient && !form.values.background?.gradient) {
                  form.setFieldValue('background.gradient', 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)');
                }
              }}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="xs" c="dimmed">
              套用範圍
            </Text>
            <SegmentedControl
              fullWidth
              data={[
                { label: '整體對話視窗 (Window)', value: WebBotBackgroundTarget.Window },
                { label: '僅聊天訊息區 (Messages)', value: WebBotBackgroundTarget.Messages },
              ]}
              {...form.getInputProps('background.target')}
              value={form.values.background?.target ?? WebBotBackgroundTarget.Window}
            />
          </Stack>

          {form.values.background?.type === WebBotBackgroundType.Color && (
            <ColorInput
              label="背景顏色"
              placeholder="預設 #ffffff (留空則使用預設白底)"
              {...form.getInputProps('background.color')}
              value={form.values.background?.color ?? ''}
            />
          )}

          {form.values.background?.type === WebBotBackgroundType.Gradient && (
            <GradientInput
              label="背景漸層設定"
              {...form.getInputProps('background.gradient')}
            />
          )}

          {form.values.background?.type === WebBotBackgroundType.Image && (
            <Stack gap="md">
              {workspaceId && botProviderId && (
                <WebBotBackgroundImageUpload
                  workspaceId={workspaceId}
                  botProviderId={botProviderId}
                />
              )}

              <Select
                label="圖片呈現模式"
                data={[
                  { label: '整張填滿 (Cover)', value: WebBotBackgroundImageMode.Cover },
                  { label: '完整顯示 (Contain)', value: WebBotBackgroundImageMode.Contain },
                  { label: '平鋪重複 (Repeat)', value: WebBotBackgroundImageMode.Repeat },
                  { label: '置中原始大小 (Center)', value: WebBotBackgroundImageMode.Center },
                ]}
                {...form.getInputProps('background.imageMode')}
                value={form.values.background?.imageMode ?? WebBotBackgroundImageMode.Cover}
              />

              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm">圖片縮放比例 (Scale)</Text>
                  <Text size="sm" c="dimmed">
                    {form.values.background?.imageScale ?? 100}%
                  </Text>
                </Group>
                <Slider
                  min={10}
                  max={300}
                  step={5}
                  mb="lg"
                  marks={[
                    { value: 50, label: '50%' },
                    { value: 100, label: '100%' },
                    { value: 200, label: '200%' },
                  ]}
                  {...form.getInputProps('background.imageScale')}
                  value={form.values.background?.imageScale ?? 100}
                />
              </Stack>

              <Group grow align="flex-start">
                <NumberInput
                  label="水平位移 (Offset X)"
                  description="向右為正 / 向左為負 (px)"
                  min={-200}
                  max={200}
                  {...form.getInputProps('background.imageOffsetX')}
                  value={form.values.background?.imageOffsetX ?? 0}
                />
                <NumberInput
                  label="垂直位移 (Offset Y)"
                  description="向下為正 / 向上為負 (px)"
                  min={-200}
                  max={200}
                  {...form.getInputProps('background.imageOffsetY')}
                  value={form.values.background?.imageOffsetY ?? 0}
                />
              </Group>
            </Stack>
          )}

          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm">背景不透明度 (Opacity)</Text>
              <Text size="sm" c="dimmed">
                {form.values.background?.opacity ?? 100}%
              </Text>
            </Group>
            <Slider
              min={0}
              max={100}
              step={1}
              mb="sm"
              marks={[
                { value: 0, label: '0%' },
                { value: 50, label: '50%' },
                { value: 100, label: '100%' },
              ]}
              {...form.getInputProps('background.opacity')}
              value={form.values.background?.opacity ?? 100}
            />
          </Stack>
        </Stack>
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Stack gap="md">
          <Text size="sm" fw={500}>
            頁首與頁尾風格 (Header & Footer)
          </Text>
          <Divider style={{ opacity: 0.6 }} />

          {workspaceId && botProviderId && (
            <WebBotAvatarUpload
              workspaceId={workspaceId}
              botProviderId={botProviderId}
            />
          )}

          <Stack gap="xs">
            <Text size="xs" c="dimmed">
              標題列風格 (Header Style)
            </Text>
            <SegmentedControl
              fullWidth
              data={[
                { label: '預設主題色', value: WebBotHeaderFooterStyle.Default },
                { label: '半透明模糊', value: WebBotHeaderFooterStyle.Frosted },
                { label: '完全透明', value: WebBotHeaderFooterStyle.Transparent },
                { label: '自訂色彩', value: WebBotHeaderFooterStyle.Custom },
              ]}
              {...form.getInputProps('header.style')}
              value={form.values.header?.style ?? WebBotHeaderFooterStyle.Default}
            />
          </Stack>

          {form.values.header?.style === WebBotHeaderFooterStyle.Frosted && (
            <Stack gap="md" p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: 8 }}>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="xs" fw={500}>
                    Header 模糊度 (Blur)
                  </Text>
                  <Text size="xs" c="dimmed">
                    {form.values.header?.blur ?? 8}px
                  </Text>
                </Group>
                <Slider
                  min={0}
                  max={30}
                  step={1}
                  mb="lg"
                  marks={[
                    { value: 0, label: '0px' },
                    { value: 8, label: '8px' },
                    { value: 15, label: '15px' },
                    { value: 30, label: '30px' },
                  ]}
                  {...form.getInputProps('header.blur')}
                  value={form.values.header?.blur ?? 8}
                />
              </Stack>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="xs" fw={500}>
                    Header 白底不透明度 (Opacity)
                  </Text>
                  <Text size="xs" c="dimmed">
                    {form.values.header?.opacity ?? 60}%
                  </Text>
                </Group>
                <Slider
                  min={0}
                  max={100}
                  step={1}
                  mb="sm"
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 50, label: '50%' },
                    { value: 100, label: '100%' },
                  ]}
                  {...form.getInputProps('header.opacity')}
                  value={form.values.header?.opacity ?? 60}
                />
              </Stack>
            </Stack>
          )}

          {form.values.header?.style === WebBotHeaderFooterStyle.Custom && (
            <Group grow>
              <ColorInput
                label="Header 背景顏色"
                placeholder="例如: #1e293b"
                {...form.getInputProps('header.backgroundColor')}
                value={form.values.header?.backgroundColor ?? ''}
              />
              <ColorInput
                label="Header 文字/圖示顏色"
                placeholder="例如: #ffffff"
                {...form.getInputProps('header.textColor')}
                value={form.values.header?.textColor ?? ''}
              />
            </Group>
          )}

          <Stack gap="xs">
            <Text size="xs" c="dimmed">
              底部輸入區風格 (Footer Style)
            </Text>
            <SegmentedControl
              fullWidth
              data={[
                { label: '預設', value: WebBotHeaderFooterStyle.Default },
                { label: '半透明模糊', value: WebBotHeaderFooterStyle.Frosted },
                { label: '完全透明', value: WebBotHeaderFooterStyle.Transparent },
                { label: '自訂色彩', value: WebBotHeaderFooterStyle.Custom },
              ]}
              {...form.getInputProps('footer.style')}
              value={form.values.footer?.style ?? WebBotHeaderFooterStyle.Default}
            />
          </Stack>

          {form.values.footer?.style === WebBotHeaderFooterStyle.Frosted && (
            <Stack gap="md" p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: 8 }}>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="xs" fw={500}>
                    Footer 模糊度 (Blur)
                  </Text>
                  <Text size="xs" c="dimmed">
                    {form.values.footer?.blur ?? 8}px
                  </Text>
                </Group>
                <Slider
                  min={0}
                  max={30}
                  step={1}
                  mb="lg"
                  marks={[
                    { value: 0, label: '0px' },
                    { value: 8, label: '8px' },
                    { value: 15, label: '15px' },
                    { value: 30, label: '30px' },
                  ]}
                  {...form.getInputProps('footer.blur')}
                  value={form.values.footer?.blur ?? 8}
                />
              </Stack>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="xs" fw={500}>
                    Footer 白底不透明度 (Opacity)
                  </Text>
                  <Text size="xs" c="dimmed">
                    {form.values.footer?.opacity ?? 60}%
                  </Text>
                </Group>
                <Slider
                  min={0}
                  max={100}
                  step={1}
                  mb="sm"
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 50, label: '50%' },
                    { value: 100, label: '100%' },
                  ]}
                  {...form.getInputProps('footer.opacity')}
                  value={form.values.footer?.opacity ?? 60}
                />
              </Stack>
            </Stack>
          )}

          {form.values.footer?.style === WebBotHeaderFooterStyle.Custom && (
            <ColorInput
              label="Footer 背景顏色"
              placeholder="例如: #1e293b"
              {...form.getInputProps('footer.backgroundColor')}
              value={form.values.footer?.backgroundColor ?? ''}
            />
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
